/**
 * stats-counter.js
 * Custom element that triggers a count-up animation for each stat
 * once the section scrolls into the viewport. Animation runs once.
 */

class StatsCounterSection extends HTMLElement {
  constructor() {
    super();
    this.hasAnimated = false;
    this.statEls = [];
    this.observer = null;
  }

  connectedCallback() {
    this.statEls = Array.from(this.querySelectorAll('[data-stat-value]'));

    if (!this.statEls.length) {
      this.classList.add('is-visible');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: no IO support, just show end values immediately.
      this.classList.add('is-visible');
      this.statEls.forEach((el) => this.setFinalValue(el));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersect(entries),
      {
        root: null,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.25
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        this.hasAnimated = true;
        this.classList.add('is-visible');
        this.runCounters();
        if (this.observer) {
          this.observer.unobserve(this);
        }
      }
    });
  }

  setFinalValue(el) {
    const end = el.getAttribute('data-end') || '0';
    const prefix = el.getAttribute('data-prefix') || '';
    const suffix = el.getAttribute('data-suffix') || '';
    el.textContent = `${prefix}${end}${suffix}`;
  }

  runCounters() {
    this.statEls.forEach((el, index) => {
      const start = parseFloat(el.getAttribute('data-start')) || 0;
      const end = parseFloat(el.getAttribute('data-end'));
      const prefix = el.getAttribute('data-prefix') || '';
      const suffix = el.getAttribute('data-suffix') || '';
      const duration = parseInt(el.getAttribute('data-duration'), 10) || 1800;

      // If end isn't a valid number (e.g. purely text stat like "Small"),
      // just settle the final text and skip numeric animation.
      if (Number.isNaN(end)) {
        return;
      }

      // Small stagger so counters don't all tick in lockstep.
      const staggerDelay = index * 100;

      window.setTimeout(() => {
        this.animateValue(el, start, end, duration, prefix, suffix);
      }, staggerDelay);
    });
  }

  animateValue(el, start, end, duration, prefix, suffix) {
    const isInteger = Number.isInteger(start) && Number.isInteger(end);
    const startTime = performance.now();

    el.classList.add('is-counting');

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      let current = start + (end - start) * eased;

      current = isInteger ? Math.round(current) : Math.round(current * 10) / 10;

      el.textContent = `${prefix}${current}${suffix}`;

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        el.textContent = `${prefix}${end}${suffix}`;
        window.setTimeout(() => el.classList.remove('is-counting'), 400);
      }
    };

    window.requestAnimationFrame(step);
  }
}

if (!customElements.get('stats-counter-section')) {
  customElements.define('stats-counter-section', StatsCounterSection);
}
